import { Duration } from 'luxon';
import { useEffect, useRef, useState, type MouseEvent as ReactMouseEvent } from 'react';

/** Clamp a timeline position percentage to the [0, 100] range. */
export const clampPercent = (percent: number): number => Math.max(0, Math.min(100, percent));

/**
 * Convert a timeline position percentage into a timestamp, rounded to the nearest second
 * (video time works in whole seconds).
 */
export const percentToTimestamp = (duration: Duration, percent: number): Duration =>
    Duration.fromMillis(Math.round((duration.toMillis() * percent) / 100_000) * 1000);

/**
 * Shared pointer-drag mechanics for the timeline tracks.
 *
 * Fixes two drag bugs that used to be copy-pasted into every track:
 * - The accumulated drag offset is clamped to [0, 100], so an item can never overshoot the
 *   timeline edges. Previously only a derived local copy was clamped, leaving the offset to
 *   pile up off-screen, so dragging back did nothing until the overshoot was "unwound".
 * - The drag ends as soon as the mouse button is no longer pressed (`e.buttons === 0`), so
 *   releasing the button outside the window no longer leaves the item glued to the cursor.
 *
 * The caller supplies the per-track behaviour:
 * - `onMove(item, position)` runs on every move with the clamped position in [0, 100].
 * - `onEnd(item)` runs once when the drag finishes.
 */
export const useTimelineDrag = <T>(onMove: (item: T, position: number) => void, onEnd: (item: T) => void) => {
    const clientX = useRef(0);
    const virtualPosition = useRef(0);
    const elementRef = useRef<HTMLElement | null>(null);
    const [item, setItem] = useState<T>();

    // Keep the latest callbacks in refs so the listener effect never re-attaches mid-drag.
    const onMoveRef = useRef(onMove);
    const onEndRef = useRef(onEnd);
    onMoveRef.current = onMove;
    onEndRef.current = onEnd;

    /** Start dragging `draggedItem`; `startPercent` is its current position on the timeline (0-100). */
    const begin = (e: ReactMouseEvent<HTMLElement>, draggedItem: T, startPercent: number) => {
        e.preventDefault();
        elementRef.current = e.currentTarget.parentElement;
        clientX.current = e.clientX;
        virtualPosition.current = clampPercent(startPercent);
        setItem(() => draggedItem);
    };

    useEffect(() => {
        if (item === undefined) {
            return;
        }

        const end = () => {
            onEndRef.current(item);
            elementRef.current = null;
            setItem(undefined);
        };

        const onMouseMove = (e: MouseEvent) => {
            // The mouseup was missed (e.g. button released outside the window): stop dragging.
            if (e.buttons === 0) {
                end();
                return;
            }

            const track = elementRef.current?.parentElement;
            if (!track) {
                return;
            }

            e.preventDefault();

            const x = clientX.current - e.clientX;
            clientX.current = e.clientX;

            const positionChange = (x / track.getBoundingClientRect().width) * 100;
            if (isNaN(positionChange)) {
                return;
            }

            virtualPosition.current = clampPercent(virtualPosition.current - positionChange);
            onMoveRef.current(item, virtualPosition.current);
        };

        const onMouseUp = (e: MouseEvent) => {
            e.preventDefault();
            end();
        };

        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
        return () => {
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
        };
    }, [item]);

    return { begin };
};
